import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Helmet } from "react-helmet";
import AOS from "aos";
import "aos/dist/aos.css";
import api from "../api";
import {
  Zap,
  Shield,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Lock,
} from "lucide-react";

const FrameworkPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  const fetchFrameworks = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await api.get("maturity/my-frameworks/");
      setFrameworks(res.data || []);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError("Failed to load your assessments. Please try again.");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchFrameworks();
  }, [user, fetchFrameworks]);

  const handleFrameworkClick = (framework) => {
    const code = framework.code.toUpperCase();
    if (code.includes("COBIT")) navigate("/cobit/instructions");
    else if (code.includes("BCM")) navigate("/bcm/instructions");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-6">
        <Helmet>
          <title>My Assessments | Loading...</title>
        </Helmet>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#FF4D00] mx-auto mb-4"></div>
          <p className="text-xl text-gray-600 font-medium">Loading your assessments...</p>
          <p className="text-gray-500 mt-2">This may take a moment</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-6">
        <Helmet>
          <title>My Assessments | Error</title>
        </Helmet>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Oops!</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchFrameworks}
            className="bg-[#FF4D00] text-white px-6 py-2 rounded-lg font-semibold hover:bg-orange-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (frameworks.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-6">
        <Helmet>
          <title>My Assessments | No Frameworks</title>
        </Helmet>
        <div className="bg-white rounded-2xl shadow-xl p-12 max-w-md w-full text-center">
          <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Assessments Yet</h2>
          <p className="text-gray-600 mb-6">
            You do not have any assessments assigned to your account yet. Please contact your administrator to request access.
          </p>
          <button
            onClick={() => navigate("/contact")}
            className="bg-[#FF4D00] text-white px-6 py-2 rounded-lg font-semibold hover:bg-orange-700 transition"
          >
            Contact Administrator
          </button>
        </div>
      </div>
    );
  }

  const getFrameworkDetails = (framework) => {
    const code = framework.code.toUpperCase();

    if (code.includes("COBIT")) {
      return {
        icon: <Zap className="w-8 h-8" />,
        gradient: "from-blue-600 to-blue-700",
        bgGradient: "from-blue-50 to-blue-100",
        description: "Governance and Management Framework",
        scopeLabel: "Assessment Scope",
        scopeValue: "40 COBIT Processes",
        timeLabel: "Estimated Duration",
        timeValue: "45-60 minutes per process assessment",
        bullets: [
          "Evidence Upload Support",
          "Auto-Generated Interim Reports",
          "Capability Scoring",
        ],
      };
    } else if (code.includes("BCM")) {
      return {
        icon: <Shield className="w-8 h-8" />,
        gradient: "from-green-600 to-green-700",
        bgGradient: "from-green-50 to-green-100",
        description: "Business Continuity Assessment Framework",
        scopeLabel: "Assessment Scope",
        scopeValue: "6 Business Continuity Processes",
        timeLabel: "Estimated Duration",
        timeValue: "45-60 minutes per process assessment",
        bullets: [
          "Evidence Upload Support",
          "Auto-Generated Interim Reports",
          "Readiness Scoring",
        ],
      };
    }

    return {
      icon: <BarChart3 className="w-8 h-8" />,
      gradient: "from-gray-600 to-gray-700",
      bgGradient: "from-gray-50 to-gray-100",
      description: "Assessment Framework",
      scopeLabel: "Framework Scope",
      scopeValue: "Multiple Assessment Areas",
      timeLabel: "Estimated Duration",
      timeValue: "Varies",
      bullets: [
        "Evidence Upload Support",
        "Auto-Generated Interim Reports",
        "Assessment Scoring",
      ],
    };
  };

  const getGridClass = () => {
    if (frameworks.length === 1) {
      return "grid grid-cols-1 lg:grid-cols-1 max-w-md";
    } else if (frameworks.length === 2) {
      return "grid grid-cols-1 md:grid-cols-2 max-w-3xl";
    } else {
      return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-6xl";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <Helmet>
        <title>My Assessments | Governance and Business Continuity Assessment Tool</title>
        <meta
          name="description"
          content="View and start your assigned governance and business continuity assessments."
        />
      </Helmet>

      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF4D00] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10" data-aos="fade-down">
          <div className="inline-block mb-6 px-4 py-2 bg-[#FF4D00]/20 text-[#FF4D00] rounded-full text-sm font-semibold">
            Welcome, {user?.name || "User"}
          </div>

          <h1 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
            Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF4D00] to-orange-600">Assessments</span>
          </h1>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-4">
            Start or continue your governance and business continuity assessments to strengthen organizational capability and resilience.
          </p>

          <p className="text-gray-500">
            Select a framework to access your assigned assessments.
          </p>
        </div>
      </section>

      <div className="w-full h-1 bg-gradient-to-r from-transparent via-[#FF4D00] to-transparent max-w-6xl mx-auto opacity-60" />

      <section className="py-20 px-6">
        <div className="flex justify-center">
          <div className={`${getGridClass()} gap-8 w-full`}>
            {frameworks.map((framework, idx) => {
              const details = getFrameworkDetails(framework);

              return (
                <div
                  key={framework.id}
                  className="group h-full flex justify-center"
                  data-aos="fade-up"
                  data-aos-delay={idx * 100}
                >
                  <div className={`bg-gradient-to-br ${details.bgGradient} rounded-2xl border border-gray-200 hover:border-[#FF4D00]/50 h-full w-full p-8 shadow-lg hover:shadow-2xl transition duration-300 transform hover:-translate-y-2 flex flex-col`}>
                    <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br ${details.gradient} text-white rounded-xl mb-6 group-hover:scale-110 transition duration-300`}>
                      {details.icon}
                    </div>

                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {framework.name}
                    </h2>
                    <p className="text-gray-600 text-sm font-medium mb-6">
                      {details.description}
                    </p>

                    <p className="text-gray-700 text-sm leading-relaxed mb-8 flex-grow">
                      {framework.description}
                    </p>

                    <div className="grid grid-cols-2 gap-4 mb-8 pt-6 border-t border-gray-200">
                      <div className="text-center">
                        <p className="text-lg font-bold text-[#FF4D00]">{details.scopeValue}</p>
                        <p className="text-xs text-gray-600 mt-1">{details.scopeLabel}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-[#FF4D00]">{details.timeValue}</p>
                        <p className="text-xs text-gray-600 mt-1">{details.timeLabel}</p>
                      </div>
                    </div>

                    <div className="space-y-3 mb-8">
                      {details.bullets.map((bullet, bulletIdx) => (
                        <div key={bulletIdx} className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle2 className="text-[#FF4D00] flex-shrink-0" size={18} />
                          <span>{bullet}</span>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={() => handleFrameworkClick(framework)}
                      className={`w-full bg-gradient-to-r ${details.gradient} text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition duration-300 flex items-center justify-center gap-2 group/btn`}
                    >
                      Open Framework
                      <ArrowRight size={20} className="group-hover/btn:translate-x-1 transition" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" data-aos="fade-up">
            How to Complete Your <span className="text-[#FF4D00]">Assessment</span>
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                number: "1",
                title: "Read Instructions",
                description: "Start with the framework instructions to understand the assessment approach.",
              },
              {
                number: "2",
                title: "Answer Questions",
                description: "Complete all questions carefully and consistently across the assessment.",
              },
              {
                number: "3",
                title: "Upload Evidence",
                description: "Attach supporting documents to strengthen and validate your responses.",
              },
              {
                number: "4",
                title: "Review Interim Report",
                description: "Review your scoring, findings, and recommendations in the generated interim report.",
              },
            ].map((step, idx) => (
              <div
                key={idx}
                className="text-center"
                data-aos="fade-up"
                data-aos-delay={idx * 100}
              >
                <div className="bg-gradient-to-br from-[#FF4D00] to-orange-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-3xl font-bold mb-4 mx-auto">
                  {step.number}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-gradient-to-r from-[#FF4D00]/10 to-orange-600/10 border-y border-[#FF4D00]/20">
        <div className="max-w-4xl mx-auto text-center" data-aos="fade-up">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Need Help Getting Started?
          </h2>
          <p className="text-gray-600 mb-8 text-lg">
            Our support team is ready to assist you with questions about your assigned assessments.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/contact")}
              className="px-8 py-3 bg-[#FF4D00] text-white rounded-lg font-semibold hover:bg-orange-700 transition"
            >
              Contact Support
            </button>
            <button
              onClick={() => navigate("/about")}
              className="px-8 py-3 border-2 border-[#FF4D00] text-[#FF4D00] rounded-lg font-semibold hover:bg-[#FF4D00]/5 transition"
            >
              Learn More
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FrameworkPage;